import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Users, FileText, Calendar, Globe, Shield, Clock, Phone, Briefcase, GraduationCap, Heart, Plane, Building, Flag, MapPin, Search, Target, Send, Plus, AlertCircle, DollarSign, TrendingUp, Award, HelpCircle, Scale } from 'lucide-react';

const UnitedKingdom = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
    viewport: { once: true }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <motion.section 
        className="bg-custom-pattern py-16 px-6 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-inter font-bold md:font-black text-acBlack leading-tight mb-6">
            United Kingdom
          </h1>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Practical guidance for work, study, family, visits, settlement, and citizenship.
          </p>
          
          <div className="bg-white rounded-lg shadow-lg p-8 text-left max-w-3xl mx-auto">
            <h2 className="text-2xl font-semibold text-acBlack mb-4">Snapshot</h2>
            <p className="text-gray-600 leading-relaxed">
              We support individuals and families through UK immigration from first entry to settlement and citizenship. We handle work and business categories, study and graduate options, family routes, visitor travel, Indefinite Leave to Remain, and naturalisation. Typical response time is 24 to 72 hours. Our London team covers EMEA. New York operates an APAC desk with evening ET overlap.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Who this is for Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Who this is for
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Professionals</h3>
              <p className="text-gray-600 text-sm">Professionals with a UK job offer or an internal transfer</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Business Founders</h3>
              <p className="text-gray-600 text-sm">Founders and senior staff expanding a business into the UK</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Students & Graduates</h3>
              <p className="text-gray-600 text-sm">Students and new graduates planning the Graduate route</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Family Members</h3>
              <p className="text-gray-600 text-sm">Spouses, partners, and children joining or remaining with family</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Plane className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Visitors</h3>
              <p className="text-gray-600 text-sm">Tourists and business visitors</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Flag className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Residents</h3>
              <p className="text-gray-600 text-sm">Residents preparing for settlement or citizenship</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* What we handle at a glance Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            What we handle at a glance
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Employment & Business</h3>
              <p className="text-gray-600 text-sm mb-4">Skilled Worker, Health and Care, Global Business Mobility routes including Senior or Specialist Worker, Graduate Trainee, UK Expansion Worker, Service Supplier, and Secondment Worker. Scale-up and other niche categories where the profile fits.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Students & Graduates</h3>
              <p className="text-gray-600 text-sm mb-4">Student route and Graduate route planning</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Family</h3>
              <p className="text-gray-600 text-sm mb-4">Partner and spouse, fiancé, child and dependent child, dependents of workers or students</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Visitors</h3>
              <p className="text-gray-600 text-sm mb-4">Standard Visitor for tourism, family visits, business meetings, short events and limited short study or training</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Settlement & Citizenship</h3>
              <p className="text-gray-600 text-sm mb-4">Indefinite Leave to Remain by work, family, or long residence. British citizenship by naturalisation</p>
            </div>
          </div>
          <p className="text-center text-gray-600 mt-8">
            We do not handle asylum or tribunal defence.
          </p>
        </div>
      </motion.section>

      {/* Outcomes Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Outcomes you can expect
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Written Plan</h3>
              <p className="text-gray-600 text-sm">Confirms the correct category, fees, realistic timelines, and key risks</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Document Pack</h3>
              <p className="text-gray-600 text-sm">Built to the rule text with clear templates for employer, school, and sponsor evidence</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Application Support</h3>
              <p className="text-gray-600 text-sm">Online application, IHS calculation and payment, appointment booking, and interview preparation where required</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Status Tracking</h3>
              <p className="text-gray-600 text-sm">Status tracking to approval with guidance on digital status (eVisa) or BRP and right to work checks</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Future Planning</h3>
              <p className="text-gray-600 text-sm">A plan for renewals, settlement timing, and citizenship steps where eligible</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Employment and Business Routes Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Employment and business routes
          </h2>
          
          <div className="mb-12">
            <h3 className="text-2xl font-inter font-semibold text-acBlack text-center mb-8">When to consider each option</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">Skilled Worker and Health and Care</h4>
                <p className="text-gray-600 text-sm">Sponsored employment that meets the correct occupation code and salary threshold</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">Global Business Mobility</h4>
                <ul className="text-gray-600 text-sm space-y-2">
                  <li>• <strong>Senior or Specialist Worker:</strong> for transfers from an overseas group company</li>
                  <li>• <strong>Graduate Trainee:</strong> for structured training programs</li>
                  <li>• <strong>UK Expansion Worker:</strong> for first employees setting up a UK branch</li>
                  <li>• <strong>Service Supplier and Secondment Worker:</strong> for defined contract or secondment scenarios</li>
                </ul>
              </div>
            </div>
            <div className="mt-6 bg-white p-6 rounded-lg shadow-sm border">
              <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">Scale-up or niche categories</h4>
              <p className="text-gray-600 text-sm">For high growth firms and specific profiles where criteria are met</p>
            </div>
          </div>

          <div className="bg-blue-50 p-8 rounded-lg">
            <h3 className="text-xl font-inter font-semibold text-acBlack mb-4">What we do</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Code Mapping</h4>
                <p className="text-gray-600 text-sm">Map the role to an eligible occupation code and confirm salary level and allowances</p>
              </div>
              <div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Sponsor Guidance</h4>
                <p className="text-gray-600 text-sm">Guide employers on sponsor licence steps and compliance if needed</p>
              </div>
              <div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Application Prep</h4>
                <p className="text-gray-600 text-sm">Prepare the Certificate of Sponsorship details, application forms, and supporting documents</p>
              </div>
              <div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Entry Support</h4>
                <p className="text-gray-600 text-sm">Book biometrics or upload appointments and prepare you for entry and onboarding</p>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-xl font-inter font-semibold text-acBlack mb-4">Dependents of workers</h3>
            <p className="text-gray-600 text-sm">We plan spouse or partner and child applications in parallel and confirm right to work and study rules for the family.</p>
          </div>
        </div>
      </motion.section>

      {/* Students and Graduates Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Students and graduates
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">Student route</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Statement of purpose, funds in acceptable format, and CAS alignment</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Submission, IHS payment, biometrics or app upload, and entry timing</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">Graduate route planning</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Eligibility after successful completion</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Switch timing, right to work rules, and how it links to Skilled Worker or settlement plans later on</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Family Routes Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Family routes
          </h2>
          
          <div className="mb-12">
            <h3 className="text-2xl font-inter font-semibold text-acBlack text-center mb-8">Common pathways</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">Partner or Spouse</h4>
                <p className="text-gray-600 text-sm">Based on marriage, civil partnership, or durable relationship</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">Fiancé</h4>
                <p className="text-gray-600 text-sm">With marriage planned within the required period</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">Child Routes</h4>
                <p className="text-gray-600 text-sm">Child and dependent child routes</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">Worker Dependents</h4>
                <p className="text-gray-600 text-sm">Dependents of workers and students with coordinated sequencing</p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-8 rounded-lg">
            <h3 className="text-xl font-inter font-semibold text-acBlack mb-4">Key elements we handle</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Relationship Evidence</h4>
                <p className="text-gray-600 text-sm">Relationship evidence and cohabitation timeline</p>
              </div>
              <div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Financial Requirements</h4>
                <p className="text-gray-600 text-sm">Financial requirement planning through salary, savings, or permitted combinations where applicable</p>
              </div>
              <div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Accommodation</h4>
                <p className="text-gray-600 text-sm">Accommodation evidence in the correct format</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Visitors Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Visitors
          </h2>
          
          <div className="bg-white p-8 rounded-lg shadow-sm border">
            <h3 className="text-xl font-inter font-semibold text-acBlack mb-6 text-center">Standard Visitor</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-6 h-6 text-acBlack" />
                </div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Purpose Coverage</h4>
                <p className="text-gray-600 text-sm">Tourism, family visits, business meetings, conferences, and short training or limited short study</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-6 h-6 text-acBlack" />
                </div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Documentation</h4>
                <p className="text-gray-600 text-sm">Purpose letters, sponsor or invitation letters, itinerary, and proof of funds</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-6 h-6 text-acBlack" />
                </div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Entry Guidance</h4>
                <p className="text-gray-600 text-sm">Guidance on permitted activities and length of stay at the border</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Settlement and Citizenship Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Settlement and citizenship
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">Indefinite Leave to Remain</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Eligibility under work, family, or long residence routes</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Day count and absence calculations</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">English language and Life in the UK test planning</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Document harvesting and application checks</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">British citizenship by naturalisation</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Residence and good character requirements</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Timing after ILR</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Ceremony and passport steps</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </motion.section>

      {/* End-to-End Process */}
      <motion.section 
        className="py-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-acBlack mb-4 font-inter">End-to-End Process</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our structured approach ensures your UK immigration application is handled professionally from start to finish.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            {[
              {
                step: "01",
                title: "Intake and Quick Screen",
                description: "Goal, immigration history, employer or family details, and target timeline assessment.",
                icon: Search
              },
              {
                step: "02", 
                title: "Strategy Call and Written Plan",
                description: "Category selection, occupation code or route criteria, evidence list, responsibilities, and realistic schedule.",
                icon: Target
              },
              {
                step: "03",
                title: "Evidence and Forms", 
                description: "Drafting and review of employer letters, sponsor documents, relationship or study evidence, and forms with name, date, and address reconciliation.",
                icon: FileText
              },
              {
                step: "04",
                title: "Submission and Appointments",
                description: "Online filing, IHS payment where applicable, biometrics or app upload, and interview preparation if required.",
                icon: Send
              },
              {
                step: "05",
                title: "Decision and Next Steps",
                description: "Digital status or BRP collection, right to work checks, registrations, renewals, and settlement planning.",
                icon: CheckCircle
              }
            ].map((process, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <process.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-acBlack text-white rounded-full flex items-center justify-center text-sm font-bold">
                    {process.step}
                  </div>
                </div>
                <h3 className="text-lg font-bold text-acBlack mb-3 font-inter">{process.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Timelines and Service Levels */}
      <motion.section 
        className="py-16 bg-gray-50"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-acBlack mb-4 font-inter">Timelines & Service Levels</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Transparent timelines and service options to match your urgency and requirements.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Evidence Pack and Forms",
                timeline: "2-6 weeks",
                description: "Depending on readiness and category",
                icon: FileText,
                features: ["Document preparation", "Form completion", "Quality review", "Submission ready"]
              },
              {
                title: "Home Office Processing",
                timeline: "Varies",
                description: "By location and season. Priority services sometimes available",
                icon: Globe,
                features: ["Standard processing", "Priority options", "Location dependent", "Seasonal variations"],
                highlight: true
              },
              {
                title: "Communication",
                timeline: "24-72 hours",
                description: "Response time, faster during booking and interview windows",
                icon: Phone,
                features: ["Regular updates", "Priority support", "Interview prep", "Status tracking"]
              }
            ].map((service, index) => (
              <div key={index} className={`bg-white rounded-lg p-6 border ${service.highlight ? 'border-blue-200 bg-blue-50' : 'border-gray-200'} hover:shadow-lg transition-shadow`}>
                <div className="text-center mb-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <service.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-acBlack mb-2 font-inter">{service.title}</h3>
                  <div className="text-2xl font-black text-blue-600 mb-2">{service.timeline}</div>
                  <p className="text-gray-600 text-sm">{service.description}</p>
                </div>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Documents Checklist */}
      <motion.section 
        className="py-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-acBlack mb-4 font-inter">Documents Checklist by Theme</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Essential documentation requirements organized by category for UK immigration applications.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Identity and Immigration",
                description: "Passports, previous visas and BRPs or eVisa records, travel history",
                icon: Shield,
                color: "blue"
              },
              {
                title: "Employment or Business", 
                description: "Job offer, contract, detailed job description, salary evidence, sponsor licence details, Certificate of Sponsorship, company registration and financials for GBM and expansion cases",
                icon: Briefcase,
                color: "green"
              },
              {
                title: "Education and Skills",
                description: "CV, qualifications, professional registrations where needed",
                icon: GraduationCap,
                color: "purple"
              },
              {
                title: "Family",
                description: "Marriage or partnership evidence, cohabitation records, birth or adoption certificates, financial and accommodation documents for partner routes",
                icon: Heart,
                color: "pink"
              },
              {
                title: "Students",
                description: "CAS, tuition payments, funds in acceptable format, statement of purpose, accommodation plan",
                icon: FileText,
                color: "indigo"
              },
              {
                title: "Character and Health",
                description: "Police certificates for required countries, tuberculosis testing for nationals where required",
                icon: Shield,
                color: "red"
              }
            ].map((category, index) => (
              <div key={index} className="bg-white rounded-lg p-6 border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <category.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-bold text-acBlack font-inter">{category.title}</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{category.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start">
              <AlertCircle className="w-6 h-6 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-bold text-acBlack mb-2">Tailored Requirements</h4>
                <p className="text-gray-700 text-sm">
                  We finalize a tailored list during strategy since formats and validity periods differ by route. 
                  Each immigration category has specific document requirements and acceptable formats.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Special Situations */}
      <motion.section 
        className="py-16 bg-gray-50"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-acBlack mb-4 font-inter">Special Situations and How We Handle Them</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Complex cases and unique circumstances that require specialized expertise and strategic approaches.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Salary or Occupation Code Issues",
                description: "We confirm the correct code and salary level early and advise on credible alternatives if the match is weak",
                icon: DollarSign
              },
              {
                title: "Sponsor Licence Questions",
                description: "We guide employers through eligibility, key personnel, and compliance records",
                icon: Building
              },
              {
                title: "Switching Inside the UK",
                description: "We confirm if your current status allows a switch and plan timing to avoid gaps",
                icon: Clock
              },
              {
                title: "Digital Status and BRP Changes",
                description: "We ensure you can access your online status and share codes for right to work and right to rent checks",
                icon: Globe
              },
              {
                title: "Previous Refusals or Overstays",
                description: "We prepare a history summary, address decision points, and involve licensed counsel where required by law",
                icon: Shield
              }
            ].map((situation, index) => (
              <div key={index} className="bg-white rounded-lg p-6 border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <situation.icon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-acBlack mb-3 font-inter">{situation.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{situation.description}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default UnitedKingdom;
